// CTW-DURMAH-STUDENT-PAINPOINTS-SEO-RESOURCE-EXPANSION-03:
// Independent learning cluster article page: making sense of legal
// language. Fully public. Decoding on-ramp, complementary to the
// memory-science learn article. Content lives in the data module.

import React from "react";
import Head from "next/head";
import { RelatedVideoLessons } from "@/components/resources/RelatedVideoLessons";
import {
  makingSenseOfLegalLanguageSections,
  makingSenseOfLegalLanguageSources,
} from "@/content/resources/makingSenseOfLegalLanguage";
import type { ResourceSection } from "@/content/resources/types";
import {
  ResourceBreadcrumbs,
  ResourceCrossLinks,
  ResourceDisclaimer,
  ResourceHero,
  ResourceSoftCta,
  SectionSourceLinks,
  SourcesSection,
} from "@/components/resources/ResourcePrimitives";

const SITE = "https://www.durmah.ai";

function SectionBlock({ section }: { section: ResourceSection }) {
  return (
    <section id={section.id} className="mb-14 scroll-mt-32">
      <h2 className="mb-4 text-2xl md:text-3xl font-black text-gray-900 dark:text-white">
        {section.heading}
      </h2>
      {section.paragraphs.map((p, i) => (
        <p key={i} className="mb-4 leading-relaxed text-gray-600 dark:text-slate-400">
          {p}
        </p>
      ))}
      {section.bullets && (
        <ul className="mb-4 space-y-3">
          {section.bullets.map((b, i) => (
            <li key={i} className="flex gap-3 leading-relaxed text-gray-600 dark:text-slate-400">
              <span aria-hidden className="mt-[10px] h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
      {section.callout && (
        <div className="mt-5 rounded-2xl border border-indigo-100 dark:border-indigo-500/20 bg-indigo-50/70 dark:bg-indigo-500/5 p-6">
          <p className="mb-1.5 text-sm font-black text-indigo-900 dark:text-indigo-300">
            {section.callout.title}
          </p>
          <p className="m-0 text-sm leading-relaxed text-indigo-900/80 dark:text-indigo-200/70">
            {section.callout.body}
          </p>
        </div>
      )}
      <SectionSourceLinks links={section.links} />
    </section>
  );
}

export default function MakingSenseOfLegalLanguagePage() {
  const canonical = `${SITE}/resources/making-sense-of-legal-language`;
  const title = "Legal Terminology for New Law Students: Making Sense of Legal Language | Durmah";
  const description =
    "Why legal English feels like a foreign language, the four kinds of difficult vocabulary law students meet, the term-of-art trap hiding in ordinary words, where to look things up, and how to decode judgments without stopping at every sentence.";

  const articleSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Legal Terminology for New Law Students: Making Sense of Legal Language",
    description,
    url: canonical,
    isAccessibleForFree: true,
    author: { "@type": "Organization", name: "Durmah" },
    publisher: {
      "@type": "Organization",
      name: "Durmah",
      logo: { "@type": "ImageObject", url: `${SITE}/android-chrome-512x512.png` },
    },
    datePublished: "2026-07-10",
    dateModified: "2026-07-10",
  });

  const breadcrumbSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Student Resources", item: `${SITE}/resources` },
      { "@type": "ListItem", position: 2, name: "Making Sense of Legal Language", item: canonical },
    ],
  });

  return (
    <div className="bg-white dark:bg-[#020617] min-h-screen flex flex-col pt-32">
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link key="route-canonical" rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta key="route-og-url" property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: articleSchema }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbSchema }} />
      </Head>

      <main className="flex-1 py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ResourceBreadcrumbs current="Making Sense of Legal Language" />
          <ResourceHero
            eyebrow="Independent learning"
            title={
              <>
                Making Sense of{" "}
                <span className="text-indigo-600 dark:text-indigo-400">Legal Language</span>
              </>
            }
            subtitle="A Latin phrase, a French survival, an abbreviation you cannot expand, and an English word that no longer means what it usually means, all in one paragraph of reading. The first-term language problem is universal, finite, and beatable with method. Here is the decoding guide."
          />

          {/* In-page navigation */}
          <nav aria-label="On this page" className="mb-12 rounded-2xl border border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-900/40 p-6">
            <p className="mb-3 text-xs font-black uppercase tracking-widest text-gray-500 dark:text-slate-500">
              On this page
            </p>
            <ul className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
              {makingSenseOfLegalLanguageSections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-sm font-medium text-indigo-700 dark:text-indigo-400 hover:underline"
                  >
                    {s.heading}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {makingSenseOfLegalLanguageSections.map((section) => (
            <SectionBlock key={section.id} section={section} />
          ))}

          <RelatedVideoLessons
            heading="Make a legal term usable"
            intro="Watch a short vocabulary check, then read the transcript and keep the source and context with your example."
            slugs={["legal-words-in-context"]}
          />
          <ResourceCrossLinks
            items={[
              {
                title: "How to learn and remember legal terms",
                description:
                  "The memory-science companion to this page: retrieval practice, spacing, and making terms stick.",
                href: "/learn/how-to-learn-legal-terms",
              },
              {
                title: "How to Read a Case",
                description:
                  "The layered reading method this page's decoding strategy plugs into.",
                href: "/resources/how-to-read-a-case",
              },
              {
                title: "Preparing for Law Seminars",
                description:
                  "Where the vocabulary gets spoken: positions, participation and saying terms out loud.",
                href: "/resources/preparing-for-law-seminars",
              },
            ]}
          />

          <SourcesSection groups={makingSenseOfLegalLanguageSources} />
          <ResourceSoftCta
            title="Meet the terms where you actually met them"
            body="Durmah's Legal Lexicon explains the terms law students really encounter, in plain English, with audio for the ones nobody wants to mispronounce, and lets you save the terms from your own lectures. The understanding stays yours. This guide stays free either way."
          />
          <ResourceDisclaimer />
        </div>
      </main>
    </div>
  );
}
